import { createStore, del, get, keys, set, values } from "idb-keyval";
import type {
  LocalSessionState,
  QueueOperation,
} from "../types";

export interface QueueStore {
  listOperations(): Promise<QueueOperation[]>;
  putOperation(operation: QueueOperation): Promise<void>;
  deleteOperation(operationId: string): Promise<void>;
  getSessionState(sessionId: string): Promise<LocalSessionState | undefined>;
  putSessionState(state: LocalSessionState): Promise<void>;
  deleteSessionState(sessionId: string): Promise<void>;
}

const queueStore = createStore("long-session-recorder-v2", "queue");
const legacyOperationStore = createStore(
  "long-session-recorder",
  "operations",
);
const MIGRATION_KEY = "meta:migrated-v1";
const SESSION_KEY_PREFIX = "session:";

function isQueueOperation(value: unknown): value is QueueOperation {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<QueueOperation>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.sessionId === "string" &&
    typeof candidate.kind === "string"
  );
}

export class BrowserQueueStore implements QueueStore {
  private migrationPromise?: Promise<void>;

  private ensureMigrated(): Promise<void> {
    this.migrationPromise ??= this.migrateLegacyOperations();
    return this.migrationPromise;
  }

  private async migrateLegacyOperations(): Promise<void> {
    if (await get<boolean>(MIGRATION_KEY, queueStore)) {
      return;
    }

    try {
      const legacyOperations =
        await values<QueueOperation>(legacyOperationStore);

      for (const operation of legacyOperations) {
        if (
          isQueueOperation(operation) &&
          !(await get(operation.id, queueStore))
        ) {
          await set(operation.id, operation, queueStore);
        }
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "NotFoundError")) {
        console.warn("Unable to migrate the legacy recording queue", {
          operation: "migrate_indexeddb_queue",
          message: error instanceof Error ? error.message : String(error),
        });
        return;
      }
    }

    await set(MIGRATION_KEY, true, queueStore);
  }

  async listOperations(): Promise<QueueOperation[]> {
    await this.ensureMigrated();
    const operationKeys = (await keys(queueStore)).filter(
      (key): key is string =>
        typeof key === "string" &&
        !key.startsWith(SESSION_KEY_PREFIX) &&
        key !== MIGRATION_KEY,
    );
    const storedValues = await Promise.all(
      operationKeys.map((key) => get<unknown>(key, queueStore)),
    );
    return storedValues.filter(isQueueOperation);
  }

  async putOperation(operation: QueueOperation): Promise<void> {
    await this.ensureMigrated();
    await set(operation.id, operation, queueStore);
  }

  async deleteOperation(operationId: string): Promise<void> {
    await this.ensureMigrated();
    await del(operationId, queueStore);
  }

  async getSessionState(
    sessionId: string,
  ): Promise<LocalSessionState | undefined> {
    await this.ensureMigrated();
    return get<LocalSessionState>(
      `${SESSION_KEY_PREFIX}${sessionId}`,
      queueStore,
    );
  }

  async putSessionState(state: LocalSessionState): Promise<void> {
    await this.ensureMigrated();
    await set(
      `${SESSION_KEY_PREFIX}${state.sessionId}`,
      state,
      queueStore,
    );
  }

  async deleteSessionState(sessionId: string): Promise<void> {
    await this.ensureMigrated();
    await del(`${SESSION_KEY_PREFIX}${sessionId}`, queueStore);
  }

  async recordPersistedChunk(
    sessionId: string,
    likelySilent: boolean,
  ): Promise<LocalSessionState> {
    const current = (await this.getSessionState(sessionId)) ?? {
      sessionId,
      serverCreated: false,
      persistedChunkCount: 0,
      likelySilentCount: 0,
    };
    const next = {
      ...current,
      persistedChunkCount: current.persistedChunkCount + 1,
      likelySilentCount:
        current.likelySilentCount + (likelySilent ? 1 : 0),
    };
    await this.putSessionState(next);
    return next;
  }
}
